import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { analyticsService } from '@/services/analyticsService';
import { consentService } from '@/services/consentService';
import { devLog, devWarn } from '@/utils/devLog';
import { redactSecretPath, redactSecretUrl, redactSecretValue } from '@/utils/redactSecretPath';
import type { AnalyticsFeature } from '@/constants/analytics/features';
import type { AnalyticsAction } from '@/constants/analytics';

/**
 * Analytics event parameters interface
 */
export interface AnalyticsEventParams {
  [key: string]: string | number | boolean | undefined;
}

/**
 * Analytics context value interface
 */
interface AnalyticsContextValue {
  /** Track a custom event */
  trackEvent: (eventName: string, params?: AnalyticsEventParams) => void;
  /** Track a click event with element details */
  trackClick: (elementType: string, elementId?: string, params?: AnalyticsEventParams) => void;
  /** Track a feature usage event. Use ANALYTICS_FEATURES/ANALYTICS_ACTIONS constants. */
  trackFeature: (featureName: AnalyticsFeature | string, action: AnalyticsAction | string, params?: AnalyticsEventParams) => void;
  /** Track an error event */
  trackError: (errorType: string, errorMessage: string, params?: AnalyticsEventParams) => void;
  /** Fire a GA4 recommended event (login, sign_up, search, generate_lead, etc.) */
  fireRecommendedEvent: (eventName: 'sign_up' | 'login' | 'search' | 'generate_lead' | 'select_content' | 'view_item' | 'view_item_list', params?: AnalyticsEventParams) => void;
}

/**
 * Default no-op functions for SSR safety and usage outside provider
 */
const defaultContextValue: AnalyticsContextValue = {
  trackEvent: () => {},
  trackClick: () => {},
  trackFeature: () => {},
  trackError: () => {},
  fireRecommendedEvent: () => {},
};

const AnalyticsContext = createContext<AnalyticsContextValue>(defaultContextValue);

/**
 * Scroll depth thresholds to track
 */
const SCROLL_THRESHOLDS = [25, 50, 75, 90, 100] as const;

/**
 * Sanitize a page-view query string for a telemetry sink, routed through the SHARED,
 * single-source-of-truth `redactSecretUrl` scrubber (CX#13 + NEW-1, §4.3 PII exfil).
 *
 * This retires the context's own `sanitizeQueryParams`, which had two defects:
 *   (CX#13) it built the sanitized output with `URLSearchParams.set(key, …)`, which
 *     OVERWRITES on a repeated key — `?tag=a&tag=b` collapsed to `?tag=b`, silently
 *     dropping the first value.
 *   (NEW-1, the important one) it inspected ONLY the parameter KEY
 *     (`SENSITIVE_PARAM_PATTERNS.some(p => keyLower.includes(p))`) and NEVER the VALUE.
 *     A benign key riding a PII value — e.g. `?utm_content=jane@fund.com` or
 *     `?ref=https://linkedin.com/in/jane-doe` — shipped the raw email / profile URL
 *     straight to GA4 `page_view.search`, bypassing the value-aware detectors
 *     (`containsEmailValue` / `isLinkedInUrlValue` / `containsEncodedPiiValue`) that the
 *     rest of the redaction layer (hash, referrer, click hrefs) already uses.
 *
 * `redactSecretUrl` closes both: its query-string path (`redactQueryString`) preserves
 * duplicate params via `.append` (not `.set`) and redacts on VALUE as well as key, by
 * construction — so this wrapper only has to compose it into the `search`-only slice of
 * the page-view shape `trackPageView` already expects.
 *
 * @param search - The query string (e.g., "?email=user@example.com&page=1")
 * @returns Sanitized query string (with leading `?` when non-empty), safe for a telemetry sink.
 */
function sanitizeSearchForAnalytics(search: string): string {
  if (!search || search === '?') return '';
  // `redactSecretUrl` accepts a bare path/URL-ish string and manually splits on the FIRST
  // `?`/`#`; feeding it a query-only string (`?a=b`) yields an empty scrubbed path plus the
  // scrubbed `?`-prefixed query, which is exactly the `search` shape this caller needs.
  const scrubbed = redactSecretUrl(search.startsWith('?') ? search : `?${search}`);
  // A PII-bearing value can trip `redactSecretUrl`'s whole-string guard (LinkedIn URL / email /
  // encoded PII anywhere in the input) and return the bare `[redacted]` sentinel instead of a
  // reconstructed `?key=value` string — that is the correct fail-safe outcome for this sink too.
  return scrubbed;
}

/**
 * Debounce delay for scroll handler in milliseconds
 */
const SCROLL_DEBOUNCE_MS = 100;

interface AnalyticsProviderProps {
  children: ReactNode;
}

/**
 * AnalyticsProvider
 *
 * Provides analytics tracking throughout the application including:
 * - Automatic page view tracking on route changes
 * - Scroll depth tracking at 25%, 50%, 75%, 90%, 100%
 * - Click tracking on buttons, links, and inputs
 * - Session start/end tracking
 * - User identification when logged in
 */
export function AnalyticsProvider({ children }: AnalyticsProviderProps) {
  const location = useLocation();
  const { user, subscription, isAdmin, organizationId } = useAuth();

  // Consent state - subscribe to changes from consent service
  const [hasAnalyticsConsent, setHasAnalyticsConsent] = useState(
    consentService.hasConsentFor('analytics')
  );

  // Track which scroll depths have been recorded for the current page
  const recordedScrollDepthsRef = useRef<Set<number>>(new Set());

  // Debounce timer ref for scroll handler
  const scrollDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track if session has been started
  const sessionStartedRef = useRef(false);

  // Track if Web Vitals have been initialized
  const webVitalsInitializedRef = useRef(false);

  // FIX-15: Deduplication set for error handler rate limiting
  const recentErrorKeysRef = useRef<Set<string>>(new Set());

  /**
   * Subscribe to consent changes
   */
  useEffect(() => {
    const unsubscribe = consentService.subscribe((state) => {
      const newConsent = state.analytics;
      setHasAnalyticsConsent(newConsent);

      if (newConsent) {
        devLog('[Analytics] Analytics consent granted, enabling tracking');
      } else {
        devLog('[Analytics] Analytics consent withdrawn, disabling tracking');
        analyticsService.disableAnalytics();
      }
    });

    return unsubscribe;
  }, []);

  // Single consolidated effect for enabling analytics when consent is granted.
  // Handles both initial consent (returning user) and newly-granted consent.
  useEffect(() => {
    if (!hasAnalyticsConsent) return;
    // FIX-R6-07: Add .catch() to prevent unhandled promise rejection
    analyticsService.enableAnalytics().then(() => {
      if (!sessionStartedRef.current) {
        sessionStartedRef.current = true;
        analyticsService.trackSessionStart();
      }
      if (!webVitalsInitializedRef.current) {
        webVitalsInitializedRef.current = true;
        analyticsService.initWebVitals();
      }
    }).catch((err) => {
      devWarn('[Analytics] Failed to enable analytics or start session:', err);
    });
  }, [hasAnalyticsConsent]);

  /**
   * Set user ID on Firebase Analytics when user logs in (only if consent given)
   */
  useEffect(() => {
    // Only set user properties if analytics consent is given
    if (!hasAnalyticsConsent) {
      return;
    }

    if (user?.uid) {
      analyticsService.setUserId(user.uid);
      analyticsService.setUserProperties({
        subscription: subscription || 'pilot',
        isAdmin: isAdmin ? 'true' : 'false',
        // `accountTier` distinguishes admin/staff vs regular user; this is a
        // SEPARATE concept from the auth `UserRole` enum and intentionally
        // does not mirror it.
        accountTier: isAdmin ? 'admin' : 'user',
        organization_id: organizationId || '',
        account_age_days: user.metadata?.creationTime
          ? String(Math.floor((Date.now() - new Date(user.metadata.creationTime).getTime()) / 86400000))
          : '0',
      });
    } else {
      // Clear user when logged out
      analyticsService.setUserId(null);
    }
  }, [user?.uid, user?.metadata?.creationTime, subscription, isAdmin, organizationId, hasAnalyticsConsent]);

  /**
   * Capture and persist UTM parameters on first page view (stored in sessionStorage)
   */
  useEffect(() => {
    if (!location.search) return;
    try {
      const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
      const existing = sessionStorage.getItem('arterra_utm');
      if (existing) return; // Only capture on first visit in session

      const params = new URLSearchParams(location.search);
      const utm: Record<string, string> = {};
      let hasUtm = false;
      for (const key of UTM_KEYS) {
        const val = params.get(key);
        if (val) {
          // §4.3 (round-10 #10-FE): sanitize the UTM VALUE before it is persisted to
          // sessionStorage. A campaign link can carry `?utm_content=jane@fund.com` (a raw
          // email) or a token-bearing URL in a UTM value; storing it raw would persist PII
          // client-side AND later ship it to GA4 on every page_view. `redactSecretValue`
          // redacts an email / LinkedIn URL / token-path / sensitive-query value while
          // leaving a benign campaign tag (`spring_launch`, `newsletter`) untouched.
          utm[key] = String(redactSecretValue(val));
          hasUtm = true;
        }
      }
      if (hasUtm) {
        sessionStorage.setItem('arterra_utm', JSON.stringify(utm));
      }
    } catch (err) {
      devWarn('[Analytics] UTM storage access failed:', err);
    }
  }, [location.search]);

  /**
   * Capture A/B experiment tokens from URL and persist in localStorage
   * URLs carry ?exp=EXPERIMENT_ID&var=VARIANT
   */
  useEffect(() => {
    if (!location.search) return;
    const params = new URLSearchParams(location.search);
    const expId = params.get('exp');
    const variant = params.get('var');

    // Validate experiment params to prevent XSS via crafted URLs
    // Require exp_ or test_ prefix to avoid treating arbitrary URL params as experiments
    const EXP_PATTERN = /^[a-zA-Z0-9_-]{1,64}$/;
    const EXP_PREFIX_PATTERN = /^(exp_|test_)/;
    if (expId && variant && EXP_PATTERN.test(expId) && EXP_PATTERN.test(variant) && EXP_PREFIX_PATTERN.test(expId)) {
      // Block experiment params that contain PII keywords
      const PII_KEYWORDS = ['customer', 'user_id', 'email', 'phone', 'ssn', 'name', 'address'];
      const containsPII = PII_KEYWORDS.some(kw => expId.toLowerCase().includes(kw) || variant.toLowerCase().includes(kw));
      if (containsPII) {
        devLog('[Analytics] Blocked experiment param containing PII keyword:', expId);
        return;
      }
      // CONSENT GATE: Only write experiment data to localStorage once analytics consent
      // has been granted. Writing before consent would persist tracking data without
      // the user's permission, violating GDPR/CCPA principles.
      if (hasAnalyticsConsent) {
        const EXPERIMENT_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
        try {
          const experiments: Record<string, { variant: string; expires: number }> = JSON.parse(
            localStorage.getItem('arterra_experiments') || '{}'
          );
          experiments[expId] = { variant, expires: Date.now() + EXPERIMENT_TTL_MS };
          localStorage.setItem('arterra_experiments', JSON.stringify(experiments));
        } catch (err) {
          devWarn('[Analytics] Experiment storage access failed:', err);
        }

        // Set as GA4 user properties for segmentation
        analyticsService.setUserProperties({
          experiment_id: expId,
          variant: variant,
        });
      }
    }
  }, [location.search, hasAnalyticsConsent]);

  // Debounce timer ref for page view tracking
  const pageViewDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Track page views on route change (only if consent given)
   * PII PROTECTION: Query parameters are sanitized to redact sensitive values
   * Debounced by 500ms to avoid rapid-fire page views from quick navigations
   */
  useEffect(() => {
    // Reset scroll depth tracking for new page
    recordedScrollDepthsRef.current.clear();

    // Clear any pending debounced page view
    if (pageViewDebounceRef.current) {
      clearTimeout(pageViewDebounceRef.current);
    }

    // Only track if analytics consent is given
    if (hasAnalyticsConsent) {
      pageViewDebounceRef.current = setTimeout(() => {
        // Include UTM params on page_view if available
        let utmParams: Record<string, string> = {};
        try {
          const stored = sessionStorage.getItem('arterra_utm');
          if (stored) utmParams = JSON.parse(stored);
        // FIX-R6-20: Log instead of bare empty catch (CCS-3 violation)
        } catch (err) {
          devWarn('[Analytics] Failed to parse stored UTM params:', err);
        }

        analyticsService.trackPageView(redactSecretPath(location.pathname), {
          // SECURITY FIX: Sanitize query params to prevent PII exposure in analytics.
          // Sensitive params (invite, email, token, password, etc.) are redacted BY KEY
          // *and* BY VALUE via the shared `redactSecretUrl` scrubber (CX#13 + NEW-1),
          // AND the PATHNAME itself is scrubbed (round-8d #3 + round-9 #4):
          // token-bearing routes like /connect/respond/:token or
          // /owner/claims/verify/:token would otherwise ship the raw single-use
          // token into GA4 page_path / page_location.
          search: sanitizeSearchForAnalytics(location.search),
          // The HASH can carry a token on a SPA deep link, and the REFERRER is a
          // full URL that may itself be a token-bearing internal route — scrub
          // both through the full-URL scrubber before they reach GA4 (round-9 #4).
          hash: redactSecretUrl(location.hash),
          referrer: document.referrer ? redactSecretUrl(document.referrer) : undefined,
          ...utmParams,
        });
      }, 500);
    }

    return () => {
      if (pageViewDebounceRef.current) {
        clearTimeout(pageViewDebounceRef.current);
      }
    };
  }, [location.pathname, location.search, location.hash, hasAnalyticsConsent]);

  /**
   * Track session end via visibilitychange (more reliable than beforeunload on mobile)
   * Debounced: only fires if the page stays hidden for 5+ seconds to avoid
   * false session-end events on brief tab switches.
   */
  useEffect(() => {
    let hiddenTimer: ReturnType<typeof setTimeout> | null = null;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && hasAnalyticsConsent) {
        hiddenTimer = setTimeout(() => {
          analyticsService.trackSessionEndBeacon();
        }, 5000); // Only fire if hidden for 5+ seconds
      } else if (hiddenTimer) {
        clearTimeout(hiddenTimer);
        hiddenTimer = null;
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (hiddenTimer) clearTimeout(hiddenTimer);
    };
  }, [hasAnalyticsConsent]);

  /**
   * Global error tracking for async errors (promise rejections, uncaught errors)
   * Catches ~60% of production errors missed by React error boundaries
   * FIX-15: Rate-limited via deduplication to prevent flooding analytics
   */
  useEffect(() => {
    if (!hasAnalyticsConsent) return;

    const shouldTrackError = (errorType: string, message: string): boolean => {
      try {
        const key = `${errorType}:${String(message).slice(0, 100)}`;
        if (recentErrorKeysRef.current.has(key)) return false;
        recentErrorKeysRef.current.add(key);
        setTimeout(() => recentErrorKeysRef.current.delete(key), 5000);
        return true;
      } catch {
        return true; // If dedup fails, allow event through
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const message = event.reason?.message || event.reason?.toString() || 'Unknown promise rejection';
      if (!shouldTrackError('unhandled_rejection', message)) return;
      analyticsService.trackError('unhandled_rejection', message, {
        // Scrub token-bearing path segments before they reach GA4 (round-8d #3).
        page_path: redactSecretPath(window.location.pathname),
      });
    };

    const handleGlobalError = (event: ErrorEvent) => {
      if (!shouldTrackError('uncaught_error', event.message)) return;
      analyticsService.trackError('uncaught_error', event.message, {
        page_path: redactSecretPath(window.location.pathname),
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleGlobalError);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleGlobalError);
    };
  }, [hasAnalyticsConsent]);

  /**
   * Calculate current scroll depth percentage
   */
  const calculateScrollDepth = useCallback((): number => {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.scrollY || document.documentElement.scrollTop;

    // Prevent division by zero
    if (documentHeight <= windowHeight) {
      return 100;
    }

    const scrollableHeight = documentHeight - windowHeight;
    const scrollPercentage = (scrollTop / scrollableHeight) * 100;

    return Math.min(Math.round(scrollPercentage), 100);
  }, []);

  /**
   * Track scroll depth at defined thresholds (only if consent given)
   */
  useEffect(() => {
    // Don't set up scroll tracking without consent
    if (!hasAnalyticsConsent) {
      return;
    }

    const handleScroll = () => {
      // Clear existing debounce timer
      if (scrollDebounceRef.current) {
        clearTimeout(scrollDebounceRef.current);
      }

      scrollDebounceRef.current = setTimeout(() => {
        const currentDepth = calculateScrollDepth();

        // Check each threshold and track if not already recorded
        for (const threshold of SCROLL_THRESHOLDS) {
          if (currentDepth >= threshold && !recordedScrollDepthsRef.current.has(threshold)) {
            recordedScrollDepthsRef.current.add(threshold);
            analyticsService.trackScrollDepth(threshold, {
              page_path: redactSecretPath(location.pathname),
              actualDepth: currentDepth,
            });
          }
        }
      }, SCROLL_DEBOUNCE_MS);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollDebounceRef.current) {
        clearTimeout(scrollDebounceRef.current);
      }
    };
  }, [location.pathname, calculateScrollDepth, hasAnalyticsConsent]);

  /**
   * Get a NON-PII element identifier for click tracking (round-9 #5b, §4.3).
   *
   * The prior implementation fell back to `aria-label` then `innerText`, which on
   * marketplace surfaces embed user-supplied PII — e.g. an owner's "Delete
   * jane@fund.com-deck.pdf" button (filename) or a "Revoke lp@fund.com" control
   * (grantee email). Those strings then persisted verbatim in the GA4 event label.
   *
   * The identifier now prefers explicitly-authored, developer-controlled,
   * PII-free attributes (`data-analytics-id`, `id`, `data-testid`). Only when NONE
   * is present does it fall back to a GENERIC, structural descriptor built from the
   * tag + role + type — never the free text / aria-label. This guarantees a click
   * label can never carry a filename or email, at the cost of coarser analytics on
   * un-annotated controls (the safe direction — instrument the control with a
   * `data-analytics-id` to get a precise label).
   */
  const getElementIdentifier = useCallback((element: HTMLElement): string | undefined => {
    const stable =
      element.getAttribute('data-analytics-id') ||
      element.id ||
      element.getAttribute('data-testid');
    if (stable) return stable;

    // No developer-authored id → GENERIC structural descriptor only. Never the
    // aria-label or innerText (those can carry a filename / email / free text).
    const tag = element.tagName.toLowerCase();
    const role = element.getAttribute('role');
    const type = element.getAttribute('type');
    return [tag, role, type].filter(Boolean).join(':') || tag || undefined;
  }, []);

  /**
   * Get element type for click tracking
   */
  const getElementType = useCallback((element: HTMLElement): string | null => {
    const tagName = element.tagName.toLowerCase();

    // Check for buttons
    if (tagName === 'button' || element.getAttribute('role') === 'button') {
      return 'button';
    }

    // Check for links
    if (tagName === 'a') {
      return 'link';
    }

    // Check for inputs
    if (tagName === 'input' || tagName === 'select' || tagName === 'textarea') {
      return 'input';
    }

    // Check for clickable elements with onClick or specific roles
    if (
      element.getAttribute('role') === 'link' ||
      element.getAttribute('role') === 'menuitem' ||
      element.getAttribute('role') === 'tab' ||
      element.getAttribute('role') === 'checkbox' ||
      element.getAttribute('role') === 'radio' ||
      element.getAttribute('role') === 'switch'
    ) {
      return element.getAttribute('role');
    }

    // Check for elements with click handlers indicated by data attribute
    if (element.getAttribute('data-clickable') === 'true') {
      return 'clickable';
    }

    return null;
  }, []);

  // Throttle ref for click handler
  const lastClickTimeRef = useRef<number>(0);

  /**
   * Track all meaningful clicks using capture phase (only if consent given)
   * Throttled to 200ms to prevent rapid-fire click events
   */
  useEffect(() => {
    // Don't set up click tracking without consent
    if (!hasAnalyticsConsent) {
      return;
    }

    const handleClick = (event: MouseEvent) => {
      const now = Date.now();
      if (now - lastClickTimeRef.current < 200) {
        return;
      }
      lastClickTimeRef.current = now;

      let target = event.target as HTMLElement | null;
      const MAX_WALK_DEPTH = 10;
      let depth = 0;

      // Walk up the DOM tree to find a meaningful element (depth-limited)
      while (target && target !== document.body && depth < MAX_WALK_DEPTH) {
        const elementType = getElementType(target);

        if (elementType) {
          const elementId = getElementIdentifier(target);

          const clickParams: Record<string, unknown> = {
            page_path: redactSecretPath(location.pathname),
            tagName: target.tagName.toLowerCase(),
          };
          // An anchor href can be a token-bearing internal route or a link with a
          // `?invite=<token>` query — scrub the FULL URL before it reaches GA4
          // (round-9 #4).
          if (target instanceof HTMLAnchorElement && target.href) {
            clickParams.href = redactSecretUrl(target.href);
          }

          // Detect outbound links
          const anchor = target.closest('a') as HTMLAnchorElement | null;
          if (anchor?.href && anchor.hostname !== window.location.hostname) {
            clickParams.is_outbound = true;
            clickParams.outbound_url = redactSecretUrl(anchor.href);
          }

          analyticsService.trackClick(elementType, elementId, clickParams);

          // Only track the first meaningful element found
          break;
        }

        target = target.parentElement;
        depth++;
      }
    };

    // Use capture phase to catch all clicks before they might be stopped
    document.addEventListener('click', handleClick, { capture: true });

    return () => {
      document.removeEventListener('click', handleClick, { capture: true });
    };
  }, [location.pathname, getElementType, getElementIdentifier, hasAnalyticsConsent]);

  /**
   * Track custom event (only if consent given)
   */
  const trackEvent = useCallback((eventName: string, params?: AnalyticsEventParams) => {
    if (!hasAnalyticsConsent) {
      devLog('[Analytics] Event blocked (no consent):', eventName);
      return;
    }
    analyticsService.trackEvent(eventName, {
      ...params,
      page_path: redactSecretPath(location.pathname),
    });
  }, [location.pathname, hasAnalyticsConsent]);

  /**
   * Track click event (manual tracking for specific interactions, only if consent given)
   */
  const trackClick = useCallback(
    (elementType: string, elementId?: string, params?: AnalyticsEventParams) => {
      if (!hasAnalyticsConsent) {
        return;
      }
      analyticsService.trackClick(elementType, elementId, {
        ...params,
        page_path: redactSecretPath(location.pathname),
      });
    },
    [location.pathname, hasAnalyticsConsent]
  );

  /**
   * Track feature usage (only if consent given)
   */
  const trackFeature = useCallback(
    (featureName: string, action: string, params?: AnalyticsEventParams) => {
      if (!hasAnalyticsConsent) {
        devLog('[Analytics] Feature blocked (no consent):', featureName, action);
        return;
      }
      analyticsService.trackFeature(featureName, action, {
        ...params,
        page_path: redactSecretPath(location.pathname),
      });
    },
    [location.pathname, hasAnalyticsConsent]
  );

  /**
   * Track error (only if consent given)
   */
  const trackError = useCallback(
    (errorType: string, errorMessage: string, params?: AnalyticsEventParams) => {
      if (!hasAnalyticsConsent) {
        return;
      }
      analyticsService.trackError(errorType, errorMessage, {
        ...params,
        page_path: redactSecretPath(location.pathname),
      });
    },
    [location.pathname, hasAnalyticsConsent]
  );

  /**
   * Fire a GA4 recommended event (only if consent given)
   */
  const fireRecommendedEvent = useCallback(
    (eventName: 'sign_up' | 'login' | 'search' | 'generate_lead' | 'select_content' | 'view_item' | 'view_item_list', params?: AnalyticsEventParams) => {
      if (!hasAnalyticsConsent) {
        return;
      }
      analyticsService.fireRecommendedEvent(eventName, {
        ...params,
        page_path: redactSecretPath(location.pathname),
      });
    },
    [location.pathname, hasAnalyticsConsent]
  );

  /**
   * Memoized context value to prevent unnecessary re-renders
   */
  const contextValue = useMemo<AnalyticsContextValue>(
    () => ({
      trackEvent,
      trackClick,
      trackFeature,
      trackError,
      fireRecommendedEvent,
    }),
    [trackEvent, trackClick, trackFeature, trackError, fireRecommendedEvent]
  );

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  );
}

/**
 * useAnalytics hook
 *
 * Provides access to analytics tracking functions.
 * Returns no-op functions if used outside AnalyticsProvider (SSR safety).
 *
 * @example
 * ```tsx
 * const { trackEvent, trackFeature, trackError } = useAnalytics();
 *
 * // Track a custom event
 * trackEvent('form_submitted', { formId: 'contact' });
 *
 * // Track feature usage
 * trackFeature('dashboard', 'filter_applied', { filterType: 'date' });
 *
 * // Track an error
 * trackError('api_error', 'Failed to load projects', { endpoint: '/api/projects' });
 * ```
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useAnalytics(): AnalyticsContextValue {
  return useContext(AnalyticsContext);
}
